// components/leads/LeadPipelineView.js
// Fase F2 · Dual-pipeline Kanban con drag&drop operativo.
// Il drag&drop è SOLO una UI sopra gli endpoint backend certificati (B1):
//  - POST /leads/{id}/call-transition
//  - POST /leads/{id}/sales-transition
//  - POST /leads/{id}/handoff
// Nessuna mutation locale/optimistic complessa: drop -> (modal) -> API -> reload.
// Backend autoritativo: 403/409/422 restano definitivi (card resta all'origine + toast).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { userApi } from '../../lib/api';
import { appointmentApi } from '../../lib/api';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { LostReasonDialog } from './LeadMoveDialogs';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable, pointerWithin, rectIntersection,
} from '@dnd-kit/core';
import {
  Loader2, PhoneCall, PhoneForwarded, UserPlus, ArrowRightCircle, XCircle,
  User, Calendar, Eye, FileText, Handshake, Building, CheckCircle, MapPin, Phone, AlertCircle, AlertTriangle, GripVertical, Clock, Plus,
} from 'lucide-react';

const CALL_COLUMNS = [
  { key: 'contattato', label: 'Contattato', icon: PhoneCall, color: 'bg-cyan-600' },
  { key: 'da_richiamare', label: 'Da Richiamare', icon: PhoneForwarded, color: 'bg-amber-600' },
  { key: 'da_assegnare', label: 'Da Assegnare', icon: UserPlus, color: 'bg-indigo-600' },
  { key: 'passato_alla_vendita', label: 'Passato alla Vendita', icon: ArrowRightCircle, color: 'bg-emerald-600' },
  { key: 'non_idoneo', label: 'Non Idoneo', icon: XCircle, color: 'bg-rose-600' },
];
const SALES_COLUMNS = [
  { key: 'assegnato', label: 'Nuovo Lead', icon: User, color: 'bg-indigo-600' },
  { key: 'appuntamento', label: 'Appuntamento', icon: Calendar, color: 'bg-purple-600' },
  { key: 'sopralluogo', label: 'Sopralluogo', icon: Eye, color: 'bg-violet-600' },
  { key: 'preventivazione', label: 'Preventivazione', icon: FileText, color: 'bg-amber-600' },
  { key: 'presentato', label: 'Presentato', icon: FileText, color: 'bg-orange-600' },
  { key: 'trattativa', label: 'Trattativa', icon: Handshake, color: 'bg-rose-600' },
  { key: 'trattativa_lunga', label: 'Trattativa Lunga', icon: Clock, color: 'bg-rose-800' },
  { key: 'contratto', label: 'Contratto', icon: Building, color: 'bg-emerald-600' },
  { key: 'convertito', label: 'Convertito', icon: CheckCircle, color: 'bg-green-700' },
  { key: 'perso', label: 'Perso', icon: XCircle, color: 'bg-red-600' },
];

// --- Config transizioni coerente con backend (state machine B1) ---
const CALL_TRANSITIONS = {
  contattato: ['da_richiamare', 'da_assegnare', 'non_idoneo', 'passato_alla_vendita'],
  da_richiamare: ['contattato', 'da_assegnare', 'non_idoneo', 'passato_alla_vendita'],
  da_assegnare: ['contattato', 'da_richiamare', 'non_idoneo', 'passato_alla_vendita'], // passato_alla_vendita SOLO via handoff
  passato_alla_vendita: [],
  non_idoneo: [],
};
const SALES_FORWARD = {
  assegnato: 'appuntamento', appuntamento: 'sopralluogo', sopralluogo: 'preventivazione',
  preventivazione: 'presentato', presentato: 'trattativa', trattativa: 'contratto', contratto: 'convertito',
};
const SALES_ROLLBACK = {
  appuntamento: 'assegnato', sopralluogo: 'appuntamento', presentato: 'preventivazione', trattativa: 'presentato',
};
const SALES_PERSO_SOURCES = ['assegnato', 'appuntamento', 'sopralluogo', 'preventivazione', 'presentato', 'trattativa', 'trattativa_lunga', 'contratto'];
const SALES_LABELS = SALES_COLUMNS.reduce((a, c) => ({ ...a, [c.key]: c.label }), {});
const CALL_LABELS = CALL_COLUMNS.reduce((a, c) => ({ ...a, [c.key]: c.label }), {});

const NON_ELIGIBLE_REASONS = [
  { value: 'non_target', label: 'Non target' },
  { value: 'zona_non_coperta', label: 'Zona non coperta' },
  { value: 'budget_incompatibile', label: 'Budget incompatibile' },
  { value: 'duplicato', label: 'Duplicato' },
  { value: 'dati_insufficienti', label: 'Dati insufficienti' },
  { value: 'non_raggiungibile', label: 'Non raggiungibile' },
  { value: 'altro', label: 'Altro' },
];

// F2.7 · Kanban column collapse / show-more (solo presentation)
const INITIAL_VISIBLE_COUNT = 8;
const LOAD_MORE_STEP = 8;

const errMsg = (e, fallback = 'Operazione non riuscita') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return fallback;
};

// Collision detection basata sul PUNTATORE (immune all'altezza delle colonne).
// closestCorners sbagliava il target quando una colonna era molto più alta delle
// vicine (es. drop su "Passato alla Vendita" apriva "Non Idoneo"). pointerWithin
// usa la posizione del cursore; fallback rectIntersection quando il puntatore non
// è dentro alcun droppable (es. gap tra le card).
const pipelineCollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return rectIntersection(args);
};

const callValidTargets = (from) => CALL_TRANSITIONS[from] || [];
// F2.1 · target Sales validi (forward + rollback approvati); convertito MAI via drag.
const SALES_TARGET_MAP = {
  assegnato: ['appuntamento'],
  appuntamento: ['sopralluogo', 'assegnato'],
  sopralluogo: ['preventivazione', 'appuntamento'],
  preventivazione: ['presentato'],
  presentato: ['trattativa', 'preventivazione'],
  trattativa: ['contratto', 'trattativa_lunga', 'presentato'],
  trattativa_lunga: ['contratto', 'trattativa'],
  contratto: [],
  convertito: [],
  perso: [],
};
const salesValidTargets = (from) => {
  const t = new Set(SALES_TARGET_MAP[from] || []);
  if (SALES_PERSO_SOURCES.includes(from)) t.add('perso');
  return Array.from(t);
};

const fmtCallback = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return { text: `${dd}/${mm} · ${hh}:${mi}`, overdue: d.getTime() < Date.now() };
  } catch { return null; }
};

const CardInner = ({ lead, pipeline, techName, dragHandle }) => {
  const stage = pipeline === 'call' ? lead.call_stage : lead.sales_stage;
  const cb = pipeline === 'call' && lead.call_stage === 'da_richiamare'
    ? fmtCallback(lead.callback?.callback_at) : null;
  const showTech = lead.assigned_technician_id && (
    (pipeline === 'call' && lead.call_stage === 'passato_alla_vendita') || pipeline === 'sales'
  );
  const badgeLabel = pipeline === 'call' ? CALL_LABELS[stage] : SALES_LABELS[stage];
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-sm text-[#3C3C3C] truncate flex-1">
          {lead.first_name} {lead.last_name}
        </div>
        {dragHandle}
      </div>
      {badgeLabel && (
        <Badge className="mt-1 bg-gray-100 text-gray-600 text-[10px]" data-testid={`card-stage-badge-${lead.id}`}>
          {badgeLabel}
        </Badge>
      )}
      {(lead.customer_city || lead.property_city || lead.customer_address) && (
        <div className="flex items-center gap-1 text-xs text-[#737373] mt-1 truncate">
          <MapPin className="w-3 h-3 flex-shrink-0" />
          {lead.customer_city || lead.property_city || lead.customer_address}
        </div>
      )}
      {lead.phone && (
        <div className="flex items-center gap-1 text-xs text-[#737373] mt-0.5">
          <Phone className="w-3 h-3" />{lead.phone}
        </div>
      )}
      {cb && (
        <div
          data-testid={`callback-badge-${lead.id}`}
          className={`flex items-center gap-1 mt-2 text-xs font-medium px-2 py-1 rounded ${
            cb.overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          }`}
        >
          <AlertCircle className="w-3 h-3" />
          Richiamare: {cb.text}{cb.overdue ? ' (scaduto)' : ''}
        </div>
      )}
      {showTech && (
        <div className="flex items-center gap-1 text-xs text-indigo-600 mt-2">
          <User className="w-3 h-3" />{techName(lead.assigned_technician_id)}
        </div>
      )}
      {pipeline === 'sales' && lead.completion_missing && (
        <div
          data-testid={`card-da-completare-${lead.id}`}
          className="flex items-center gap-1 mt-2 text-xs font-medium px-2 py-1 rounded bg-amber-100 text-amber-800"
        >
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          {lead.completion_missing === 'pm' ? 'PM mancante'
            : lead.completion_missing === 'assignee' ? 'Assegnatario mancante'
            : 'Assegnatario e PM mancanti'}
        </div>
      )}
    </>
  );
};

const DraggableCard = ({ lead, pipeline, techName, onClick, draggable }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
    data: { lead, pipeline },
    disabled: !draggable,
  });
  return (
    <div
      ref={setNodeRef}
      onClick={() => onClick(lead)}
      data-testid={`pipeline-card-${lead.id}`}
      className={`bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md hover:border-[#FF7819] transition-all ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      <CardInner
        lead={lead}
        pipeline={pipeline}
        techName={techName}
        dragHandle={draggable ? (
          <button
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-300 hover:text-[#FF7819] cursor-grab active:cursor-grabbing shrink-0"
            data-testid={`drag-handle-${lead.id}`}
            aria-label="Trascina"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        ) : null}
      />
    </div>
  );
};

const DroppableColumn = ({ col, leads, pipeline, techName, onCardClick, draggableFor, dragActive, isValidTarget, resetSignal }) => {
  const Icon = col.icon;
  const { setNodeRef, isOver } = useDroppable({ id: `col-${col.key}`, data: { stage: col.key } });
  const dimmed = dragActive && !isValidTarget;

  // F2.7 · stato show-more INDIPENDENTE per colonna. Reset su cambio filtro/ricerca/pipeline.
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  useEffect(() => { setVisibleCount(INITIAL_VISIBLE_COUNT); }, [resetSignal]);

  const total = leads.length;
  const visibleLeads = leads.slice(0, visibleCount);
  const hiddenCount = Math.max(0, total - visibleCount);
  const showMore = hiddenCount > 0;
  const showLess = !showMore && total > INITIAL_VISIBLE_COUNT;

  return (
    <div className="w-72 flex-shrink-0" data-testid={`pipeline-column-${col.key}`}>
      <div className={`rounded-t-xl p-3 ${col.color} ${dimmed ? 'opacity-40' : ''}`}>
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-2"><Icon className="w-4 h-4" /><span className="font-medium text-sm">{col.label}</span></div>
          <Badge className="bg-white/20 text-white text-xs" data-testid={`col-count-${col.key}`}>{total}</Badge>
        </div>
      </div>
      <div
        ref={setNodeRef}
        data-testid={`droppable-${col.key}`}
        className={`rounded-b-xl p-3 min-h-[400px] space-y-3 transition-all ${
          dimmed ? 'bg-gray-100 opacity-50' : 'bg-gray-50'
        } ${isOver && isValidTarget ? 'ring-2 ring-[#FF7819] ring-inset bg-orange-50' : ''} ${
          isOver && dragActive && !isValidTarget ? 'ring-2 ring-red-300 ring-inset' : ''
        }`}
      >
        {total === 0
          ? <div className="text-center py-8 text-[#999]"><p className="text-sm">{isOver && isValidTarget ? 'Rilascia qui' : 'Nessun lead'}</p></div>
          : (
            <>
              {visibleLeads.map((l) => (
                <DraggableCard
                  key={l.id}
                  lead={l}
                  pipeline={pipeline}
                  techName={techName}
                  onClick={onCardClick}
                  draggable={draggableFor(l)}
                />
              ))}
              {showMore && (
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + LOAD_MORE_STEP)}
                  data-testid={`show-more-${col.key}`}
                  className="w-full text-center text-xs text-[#737373] hover:text-[#FF7819] py-2 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Mostra altri {hiddenCount}
                </button>
              )}
              {showLess && (
                <button
                  type="button"
                  onClick={() => setVisibleCount(INITIAL_VISIBLE_COUNT)}
                  data-testid={`show-less-${col.key}`}
                  className="w-full text-center text-xs text-[#999] hover:text-[#FF7819] py-2 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Riduci
                </button>
              )}
            </>
          )}
      </div>
    </div>
  );
};

const LeadPipelineView = () => {
  const { isAdmin, isProjectManager, isTechnician, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canSeeBoth = isAdmin || isProjectManager;

  const urlPipeline = searchParams.get('pipeline');
  const pipeline = isTechnician ? 'sales' : (urlPipeline === 'sales' ? 'sales' : 'call');

  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState(null);
  const [techs, setTechs] = useState([]);
  const [assignableWork, setAssignableWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [daCompletareOnly, setDaCompletareOnly] = useState(false);

  // DnD state
  const [activeLead, setActiveLead] = useState(null);
  const [busy, setBusy] = useState(false);
  // Pending drop action → apre dialog
  const [callbackCtx, setCallbackCtx] = useState(null);   // {lead}
  const [nonEligibleCtx, setNonEligibleCtx] = useState(null);
  const [handoffCtx, setHandoffCtx] = useState(null);
  const [persoCtx, setPersoCtx] = useState(null);
  const [trattativaLungaCtx, setTrattativaLungaCtx] = useState(null); // {lead}
  const [tlReason, setTlReason] = useState('');
  // Blocco B · workflow appuntamento (sopralluogo / presentazione preventivo)
  const [apptCtx, setApptCtx] = useState(null); // {lead, apptType, title}
  const [apDate, setApDate] = useState('');
  const [apDuration, setApDuration] = useState('60');
  const [apNotes, setApNotes] = useState('');
  const [apSubmitting, setApSubmitting] = useState(false);
  // F2.3 · creazione lead da pipeline
  const [createOpen, setCreateOpen] = useState(false);
  const [cPipeline, setCPipeline] = useState('call');
  const [cForm, setCForm] = useState({ first_name: '', last_name: '', phone: '', email: '', customer_city: '', notes: '' });
  const [cTech, setCTech] = useState('');
  const [cPM, setCPM] = useState('');
  const [cSubmitting, setCSubmitting] = useState(false);
  // Dialog fields
  const [cbAt, setCbAt] = useState('');
  const [cbNote, setCbNote] = useState('');
  const [neReason, setNeReason] = useState('');
  const [neNote, setNeNote] = useState('');
  const [handoffTech, setHandoffTech] = useState('');
  const [handoffPM, setHandoffPM] = useState('');
  const [handoffNotes, setHandoffNotes] = useState('');
  const [persoSubmitting, setPersoSubmitting] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const columns = pipeline === 'call' ? CALL_COLUMNS : SALES_COLUMNS;

  useEffect(() => {
    if (isTechnician && urlPipeline !== 'sales') {
      setSearchParams({ pipeline: 'sales' }, { replace: true });
    } else if (canSeeBoth && !urlPipeline) {
      setSearchParams({ pipeline: 'call' }, { replace: true });
    }
  }, [isTechnician, canSeeBoth, urlPipeline, setSearchParams]);

  const techName = useCallback((id) => {
    const t = techs.find((x) => x.id === id);
    return t ? `${t.first_name} ${t.last_name}` : 'Tecnico';
  }, [techs]);

  const activeTechs = useMemo(
    () => (techs || []).filter((t) => t.role === 'external_technician' && t.is_active !== false),
    [techs]
  );

  // HOTFIX · PM selezionabili: riuso /users/assignable?type=work filtrato a project_manager attivi
  const activePMs = useMemo(
    () => (assignableWork || []).filter((u) => u.role === 'project_manager' && u.is_active !== false),
    [assignableWork]
  );

  // Sales Assignment Hardening · assegnatario = Tecnico OPPURE Project Manager.
  const assigneeOptions = useMemo(() => ([
    ...activeTechs.map((t) => ({ id: t.id, label: `${t.first_name} ${t.last_name}`, isPM: false })),
    ...activePMs.map((p) => ({ id: p.id, label: `${p.first_name} ${p.last_name}`, isPM: true })),
  ]), [activeTechs, activePMs]);
  const isPMId = useCallback((id) => activePMs.some((p) => p.id === id), [activePMs]);
  const pmLabel = useCallback((id) => {
    const p = activePMs.find((x) => x.id === id);
    return p ? `${p.first_name} ${p.last_name}` : '';
  }, [activePMs]);

  // Quando l'assegnatario è un PM → gestione diretta: PM = assegnatario (auto-fill + lock).
  const onAssigneeChange = useCallback((value, setAssignee, currentAssignee, pm, setPM) => {
    setAssignee(value);
    if (isPMId(value)) setPM(value);
    else if (pm && pm === currentAssignee) setPM(''); // era auto-compilato da gestione diretta → azzera
  }, [isPMId]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [leadsRes, countsRes] = await Promise.all([
        api.get('/leads', { params: { pipeline } }),
        api.get('/leads/pipeline-counts'),
      ]);
      setLeads(leadsRes.data);
      setCounts(countsRes.data);
    } catch (e) {
      const code = e?.response?.status;
      setError(code === 403 ? 'forbidden' : 'error');
    } finally { setLoading(false); }
  }, [pipeline]);

  // Refresh SOLO leads+counts senza spinner globale (post-transizione)
  const refresh = useCallback(async () => {
    try {
      const [leadsRes, countsRes] = await Promise.all([
        api.get('/leads', { params: { pipeline } }),
        api.get('/leads/pipeline-counts'),
      ]);
      setLeads(leadsRes.data);
      setCounts(countsRes.data);
    } catch { /* toast già mostrato dal chiamante */ }
  }, [pipeline]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { userApi.getTechnicians().then((r) => setTechs(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/users/assignable', { params: { type: 'work' } }).then((r) => setAssignableWork(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { setStageFilter('all'); setDaCompletareOnly(false); }, [pipeline]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return leads.filter((l) => {
      const matchSearch = !q ||
        `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) ||
        (l.customer_city || l.property_city || '').toLowerCase().includes(q) ||
        (l.phone || '').includes(search);
      const stage = pipeline === 'call' ? l.call_stage : l.sales_stage;
      const matchStage = stageFilter === 'all' || stage === stageFilter;
      const matchCompletion = !daCompletareOnly || (pipeline === 'sales' && !!l.completion_missing);
      return matchSearch && matchStage && matchCompletion;
    });
  }, [leads, search, stageFilter, pipeline, daCompletareOnly]);

  const grouped = useMemo(() => {
    const key = pipeline === 'call' ? 'call_stage' : 'sales_stage';
    return columns.reduce((acc, c) => {
      acc[c.key] = filtered.filter((l) => l[key] === c.key);
      return acc;
    }, {});
  }, [filtered, columns, pipeline]);

  // Chi può trascinare una card
  const canDrag = useCallback((lead) => {
    if (busy) return false;
    if (pipeline === 'call') {
      if (!canSeeBoth) return false;
      return callValidTargets(lead.call_stage).length > 0; // terminali non draggabili
    }
    // sales
    const owned = lead.assigned_technician_id === user?.id || lead.assigned_work_user_id === user?.id;
    if (isTechnician && !owned) return false;
    return salesValidTargets(lead.sales_stage).length > 0;
  }, [pipeline, canSeeBoth, isTechnician, user, busy]);

  // Target validi per la card in drag (per highlight colonne)
  const activeValidTargets = useMemo(() => {
    if (!activeLead) return [];
    return pipeline === 'call'
      ? callValidTargets(activeLead.call_stage)
      : salesValidTargets(activeLead.sales_stage);
  }, [activeLead, pipeline]);

  // --- API callers ---
  const doCallTransition = async (lead, toStage, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/call-transition`, { to_stage: toStage, ...extra });
      toast.success(`Chiamata → ${CALL_LABELS[toStage]}`);
      await refresh();
      return true;
    } catch (e) { toast.error(errMsg(e, 'Transizione Chiamata non riuscita')); return false; }
    finally { setBusy(false); }
  };
  const doSalesTransition = async (lead, toStage, notes = null) => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/sales-transition`, { to_stage: toStage, notes });
      toast.success(`Vendita → ${SALES_LABELS[toStage]}`);
      await refresh();
      return true;
    } catch (e) { toast.error(errMsg(e, 'Transizione Vendita non riuscita')); return false; }
    finally { setBusy(false); }
  };

  // --- Drag end ---
  const handleDragStart = (event) => {
    const l = event.active?.data?.current?.lead;
    setActiveLead(l || null);
  };
  const handleDragEnd = (event) => {
    const lead = activeLead;
    setActiveLead(null);
    const overId = event.over?.id;
    if (!lead || !overId) return;
    const target = String(overId).replace(/^col-/, '');
    if (pipeline === 'call') {
      const from = lead.call_stage;
      if (from === target) return;
      if (!callValidTargets(from).includes(target)) return; // drop non valido → ignora
      if (target === 'da_richiamare') { setCbAt(''); setCbNote(''); setCallbackCtx({ lead }); return; }
      if (target === 'non_idoneo') { setNeReason(''); setNeNote(''); setNonEligibleCtx({ lead }); return; }
      if (target === 'passato_alla_vendita') {
        // F2.5 · handoff diretto anche da contattato/da_richiamare (oltre a da_assegnare)
        if (!['contattato', 'da_richiamare', 'da_assegnare'].includes(from)) return;
        setHandoffTech(''); setHandoffPM(''); setHandoffNotes(''); setHandoffCtx({ lead }); return;
      }
      doCallTransition(lead, target);
    } else {
      const from = lead.sales_stage;
      if (from === target) return;
      if (target === 'convertito') return; // disabilitato
      if (!salesValidTargets(from).includes(target)) return;
      if (target === 'perso') { setPersoCtx({ lead }); return; }
      if (target === 'trattativa_lunga') { setTlReason(''); setTrattativaLungaCtx({ lead }); return; }
      // Blocco B · transizioni appointment-driven (no sales-transition diretta)
      if (from === 'assegnato' && target === 'appuntamento') {
        setApDate(''); setApDuration('60'); setApNotes('');
        setApptCtx({ lead, apptType: 'sopralluogo', title: 'Fissa sopralluogo', opKey: `appointment_pipeline:${lead.id}:sopralluogo:${(crypto.randomUUID && crypto.randomUUID()) || Date.now()}` });
        return;
      }
      if (from === 'preventivazione' && target === 'presentato') {
        setApDate(''); setApDuration('60'); setApNotes('');
        setApptCtx({ lead, apptType: 'presentazione_preventivo', title: 'Fissa presentazione preventivo', opKey: `appointment_pipeline:${lead.id}:presentazione_preventivo:${(crypto.randomUUID && crypto.randomUUID()) || Date.now()}` });
        return;
      }
      doSalesTransition(lead, target);
    }
  };

  // --- Dialog submit ---
  const submitCallback = async () => {
    if (!cbAt) { toast.error('Seleziona data e ora del richiamo'); return; }
    const iso = new Date(cbAt).toISOString();
    const ok = await doCallTransition(callbackCtx.lead, 'da_richiamare', { callback_at: iso, callback_note: cbNote || null });
    if (ok) setCallbackCtx(null);
  };
  const submitNonEligible = async () => {
    if (!neReason) { toast.error('Seleziona un motivo'); return; }
    if (neReason === 'altro' && !neNote.trim()) { toast.error('Nota obbligatoria quando il motivo è "Altro"'); return; }
    const ok = await doCallTransition(nonEligibleCtx.lead, 'non_idoneo', { non_eligible_reason: neReason, non_eligible_note: neNote || null });
    if (ok) setNonEligibleCtx(null);
  };
  const submitHandoff = async () => {
    if (!handoffTech) { toast.error('Seleziona un assegnatario (Tecnico o Project Manager)'); return; }
    if (!isPMId(handoffTech) && !handoffPM) { toast.error('Il Project Manager è obbligatorio per un lead assegnato a un tecnico'); return; }
    setBusy(true);
    try {
      await api.post(`/leads/${handoffCtx.lead.id}/handoff`, { technician_id: handoffTech, project_manager_id: handoffPM || null, notes: handoffNotes || null });
      toast.success('Handoff completato: lead passato alla Vendita');
      setHandoffCtx(null); setHandoffTech(''); setHandoffPM(''); setHandoffNotes('');
      await refresh();
    } catch (e) { toast.error(errMsg(e, 'Handoff non riuscito')); }
    finally { setBusy(false); }
  };
  const submitPerso = async (leadId, _toStatus, form) => {
    setPersoSubmitting(true);
    const label = (form?.reason || '').replace(/_/g, ' ');
    const notes = `Perso — motivo: ${label}${form?.notes ? ` · ${form.notes}` : ''}`;
    const ok = await doSalesTransition(persoCtx.lead, 'perso', notes);
    setPersoSubmitting(false);
    if (ok) setPersoCtx(null);
  };

  const submitTrattativaLunga = async () => {
    const reason = (tlReason || '').trim();
    if (reason.length < 5) { toast.error('Motivazione obbligatoria (min 5 caratteri)'); return; }
    const ok = await doSalesTransition(trattativaLungaCtx.lead, 'trattativa_lunga', reason);
    if (ok) setTrattativaLungaCtx(null);
  };

  const submitAppointment = async () => {
    if (!apDate) { toast.error('Seleziona data e ora'); return; }
    const when = new Date(apDate);
    if (isNaN(when.getTime()) || when.getTime() <= Date.now()) {
      toast.error('La data/ora deve essere futura'); return;
    }
    const lead = apptCtx.lead;
    const tech = techs.find((t) => t.id === lead.assigned_technician_id);
    setApSubmitting(true);
    try {
      const participants_internal = tech
        ? [{ user_id: tech.id, name: `${tech.first_name} ${tech.last_name}`.trim(), email: tech.email || '', role: 'external_technician', send_email: !!tech.email }]
        : [];
      const apptResp = await appointmentApi.create({
        lead_id: lead.id,
        assigned_technician_id: lead.assigned_technician_id,
        appointment_date: when.toISOString(),
        appointment_type: apptCtx.apptType,
        duration_minutes: parseInt(apDuration, 10) || 60,
        notes: apNotes || null,
        send_confirmation_email: true,
        participants_internal,
        idempotency_key: apptCtx.opKey,
      });
      const okLabel = apptCtx.apptType === 'sopralluogo' ? 'Sopralluogo fissato' : 'Presentazione preventivo fissata';
      const clientEmailFailed = lead.email && apptResp?.data?.client_email_status === 'failed';
      if (clientEmailFailed) {
        toast.warning(`${okLabel} — invio email al cliente non riuscito`);
      } else {
        toast.success(okLabel);
      }
      setApptCtx(null);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Creazione appuntamento non riuscita'));
    } finally { setApSubmitting(false); }
  };

  const switchPipeline = (p) => setSearchParams({ pipeline: p });
  const openCreate = () => {
    setCForm({ first_name: '', last_name: '', phone: '', email: '', customer_city: '', notes: '' });
    setCTech(''); setCPM(''); setCPipeline(isTechnician ? 'sales' : pipeline); setCreateOpen(true);
  };
  const submitCreate = async () => {
    if (!cForm.first_name.trim() || !cForm.last_name.trim() || !cForm.phone.trim()) {
      toast.error('Nome, cognome e telefono sono obbligatori'); return;
    }
    if (cPipeline === 'sales' && canSeeBoth) {
      if (!cTech) { toast.error('Seleziona un assegnatario (Tecnico o Project Manager)'); return; }
      if (!isPMId(cTech) && !cPM) { toast.error('Il Project Manager è obbligatorio per un lead assegnato a un tecnico'); return; }
    }
    setCSubmitting(true);
    try {
      await api.post('/leads/pipeline-create', {
        pipeline: cPipeline,
        first_name: cForm.first_name.trim(),
        last_name: cForm.last_name.trim(),
        phone: cForm.phone.trim(),
        email: cForm.email.trim() || null,
        customer_city: cForm.customer_city.trim() || null,
        notes: cForm.notes.trim() || null,
        assigned_technician_id: (cPipeline === 'sales' && canSeeBoth) ? cTech : null,
        project_manager_id: (cPipeline === 'sales' && canSeeBoth) ? (cPM || null) : null,
      });
      toast.success('Lead creato');
      setCreateOpen(false);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Creazione lead non riuscita'));
    } finally { setCSubmitting(false); }
  };
  const totalLabel = pipeline === 'call' ? counts?.call_total : counts?.sales_total;
  const dragActive = !!activeLead;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-pipeline-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h2">Lead</h1>
          <p className="text-body-small mt-1">Pipeline {pipeline === 'call' ? 'Chiamata / Prequalifica' : 'Vendita / Tecnica'}</p>
        </div>
        <Button className="btn-primary" onClick={openCreate} data-testid="add-lead-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Aggiungi Lead
        </Button>
      </div>

      {canSeeBoth && (
        <div className="flex bg-gray-100 rounded-lg p-1 w-fit" data-testid="pipeline-tabs">
          <button
            onClick={() => switchPipeline('call')}
            data-testid="tab-call"
            className={`px-5 py-2 text-sm font-medium rounded-md transition-colors ${pipeline === 'call' ? 'bg-white shadow text-[#3C3C3C]' : 'text-[#737373]'}`}
          >Chiamata{counts ? ` (${counts.call_total ?? 0})` : ''}</button>
          <button
            onClick={() => switchPipeline('sales')}
            data-testid="tab-sales"
            className={`px-5 py-2 text-sm font-medium rounded-md transition-colors ${pipeline === 'sales' ? 'bg-white shadow text-[#3C3C3C]' : 'text-[#737373]'}`}
          >Vendita{counts ? ` (${counts.sales_total ?? 0})` : ''}</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-2" data-testid="pipeline-kpi">
          <Badge className="bg-[#FF7819] text-white">Totale: {totalLabel ?? '—'}</Badge>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Input
            placeholder="Cerca nome, città, telefono…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
            data-testid="pipeline-search"
          />
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            data-testid="pipeline-stage-filter"
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">Tutti gli stage</option>
            {columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {pipeline === 'sales' && canSeeBoth && (counts?.sales_da_completare ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => setDaCompletareOnly((v) => !v)}
              data-testid="pipeline-da-completare-chip"
              aria-pressed={daCompletareOnly}
              className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                daCompletareOnly
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              Da completare
              <span className="font-bold" data-testid="pipeline-da-completare-count">{counts.sales_da_completare}</span>
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-64" data-testid="pipeline-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF7819]" />
        </div>
      )}
      {!loading && error === 'forbidden' && (
        <div className="text-center py-16 text-[#737373]" data-testid="pipeline-403">Non hai accesso a questa pipeline.</div>
      )}
      {!loading && error === 'error' && (
        <div className="text-center py-16 text-red-600" data-testid="pipeline-error">
          Errore nel caricamento dei lead. <button onClick={load} className="underline">Riprova</button>
        </div>
      )}
      {!loading && !error && (
        <DndContext sensors={sensors} collisionDetection={pipelineCollisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveLead(null)}>
          <div className="overflow-x-auto pb-4" data-testid="pipeline-kanban">
            <div className="flex gap-4 min-w-max">
              {columns.map((col) => (
                <DroppableColumn
                  key={col.key}
                  col={col}
                  leads={grouped[col.key] || []}
                  pipeline={pipeline}
                  techName={techName}
                  onCardClick={(l) => navigate(`/leads/${l.id}`)}
                  draggableFor={canDrag}
                  dragActive={dragActive}
                  isValidTarget={activeValidTargets.includes(col.key)}
                  resetSignal={`${pipeline}|${search}|${stageFilter}|${daCompletareOnly}`}
                />
              ))}
            </div>
          </div>
          <DragOverlay>
            {activeLead ? (
              <div className="bg-white rounded-lg border-2 border-[#FF7819] p-3 w-72 shadow-lg">
                <CardInner lead={activeLead} pipeline={pipeline} techName={techName} dragHandle={null} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Dialog Callback */}
      <Dialog open={!!callbackCtx} onOpenChange={(o) => !o && setCallbackCtx(null)}>
        <DialogContent className="sm:max-w-md" data-testid="callback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PhoneForwarded className="w-5 h-5 text-amber-600" /> Pianifica richiamo</DialogTitle>
            <DialogDescription>Imposta quando richiamare il lead (data futura obbligatoria).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Data e ora richiamo *</Label>
              <Input type="datetime-local" value={cbAt} onChange={(e) => setCbAt(e.target.value)} data-testid="callback-at-input" />
            </div>
            <div className="space-y-2">
              <Label>Nota (opzionale)</Label>
              <Textarea value={cbNote} onChange={(e) => setCbNote(e.target.value)} rows={2} data-testid="callback-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallbackCtx(null)} disabled={busy}>Annulla</Button>
            <Button className="btn-primary" onClick={submitCallback} disabled={busy || !cbAt} data-testid="callback-confirm-btn">
              {busy ? 'Elaboro...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Non Idoneo */}
      <Dialog open={!!nonEligibleCtx} onOpenChange={(o) => !o && setNonEligibleCtx(null)}>
        <DialogContent className="sm:max-w-md" data-testid="non-eligible-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600"><XCircle className="w-5 h-5" /> Segna Non Idoneo</DialogTitle>
            <DialogDescription>Indica il motivo dell'esclusione dalla pipeline.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Select value={neReason} onValueChange={setNeReason}>
                <SelectTrigger data-testid="non-eligible-reason-select"><SelectValue placeholder="Seleziona motivo" /></SelectTrigger>
                <SelectContent>
                  {NON_ELIGIBLE_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nota {neReason === 'altro' ? '*' : '(opzionale)'}</Label>
              <Textarea value={neNote} onChange={(e) => setNeNote(e.target.value)} rows={2} data-testid="non-eligible-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNonEligibleCtx(null)} disabled={busy}>Annulla</Button>
            <Button variant="destructive" onClick={submitNonEligible} disabled={busy} data-testid="non-eligible-confirm-btn">
              {busy ? 'Elaboro...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Handoff */}
      <Dialog open={!!handoffCtx} onOpenChange={(o) => { if (!o) { setHandoffCtx(null); setHandoffTech(''); setHandoffPM(''); setHandoffNotes(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="handoff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Handshake className="w-5 h-5 text-[#FF7819]" /> Passa alla Vendita</DialogTitle>
            <DialogDescription>
              Assegna un tecnico oppure un Project Manager: il lead passerà atomicamente a <strong>Passato alla Vendita</strong> (Chiamata) e <strong>Assegnato</strong> (Vendita).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Assegnatario *</Label>
              <Select value={handoffTech} onValueChange={(v) => onAssigneeChange(v, setHandoffTech, handoffTech, handoffPM, setHandoffPM)}>
                <SelectTrigger data-testid="handoff-assignee-select"><SelectValue placeholder="Seleziona Tecnico o Project Manager" /></SelectTrigger>
                <SelectContent>
                  {assigneeOptions.length === 0 && <SelectItem value="__none" disabled>Nessun assegnatario disponibile</SelectItem>}
                  {activeTechs.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Tecnici</SelectLabel>
                      {activeTechs.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                    </SelectGroup>
                  )}
                  {activePMs.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Project Manager (gestione diretta)</SelectLabel>
                      {activePMs.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>
            {isPMId(handoffTech) ? (
              <div className="p-2.5 bg-[#FFF3EA] border border-[#FFD9BF] rounded text-xs text-[#C24E00] flex items-center gap-2" data-testid="handoff-direct-management">
                <Handshake className="w-4 h-4 shrink-0" />
                <span>Gestione diretta: <strong>{pmLabel(handoffTech)}</strong> è assegnatario e Project Manager.</span>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Project Manager *</Label>
                <Select value={handoffPM || ''} onValueChange={setHandoffPM}>
                  <SelectTrigger data-testid="handoff-pm-select"><SelectValue placeholder="Seleziona Project Manager" /></SelectTrigger>
                  <SelectContent>
                    {activePMs.length === 0 && <SelectItem value="__none" disabled>Nessun PM attivo</SelectItem>}
                    {activePMs.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea value={handoffNotes} onChange={(e) => setHandoffNotes(e.target.value)} rows={2} data-testid="handoff-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandoffCtx(null)} disabled={busy}>Annulla</Button>
            <Button className="btn-primary" onClick={submitHandoff} disabled={busy || !handoffTech || (!isPMId(handoffTech) && !handoffPM)} data-testid="handoff-confirm-btn">
              {busy ? 'Elaboro...' : 'Conferma handoff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Perso (riuso motivo perdita) */}
      <LostReasonDialog
        open={!!persoCtx}
        onOpenChange={(o) => !o && setPersoCtx(null)}
        lead={persoCtx?.lead}
        onConfirm={submitPerso}
        isSubmitting={persoSubmitting}
      />

      {/* Dialog Trattativa Lunga (motivazione obbligatoria) */}      <Dialog open={!!trattativaLungaCtx} onOpenChange={(o) => !o && setTrattativaLungaCtx(null)}>
        <DialogContent className="sm:max-w-md" data-testid="trattativa-lunga-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-rose-800" /> Passa a Trattativa Lunga</DialogTitle>
            <DialogDescription>Indica la motivazione (obbligatoria) per cui la trattativa è considerata lunga.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivazione *</Label>
            <Textarea
              value={tlReason}
              onChange={(e) => setTlReason(e.target.value)}
              rows={3}
              placeholder="Es. cliente in attesa di finanziamento, permessi in corso…"
              data-testid="trattativa-lunga-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrattativaLungaCtx(null)} disabled={busy}>Annulla</Button>
            <Button className="btn-primary" onClick={submitTrattativaLunga} disabled={busy || (tlReason || '').trim().length < 5} data-testid="trattativa-lunga-confirm-btn">
              {busy ? 'Elaboro...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Appuntamento (Sopralluogo / Presentazione preventivo) — Blocco B */}      <Dialog open={!!apptCtx} onOpenChange={(o) => !o && setApptCtx(null)}>
        <DialogContent className="sm:max-w-md" data-testid="appointment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#FF7819]" /> {apptCtx?.title}
            </DialogTitle>
            <DialogDescription>
              {apptCtx?.apptType === 'sopralluogo'
                ? "Fissa il sopralluogo: alla conferma l'appuntamento farà avanzare il lead ad Appuntamento."
                : "Fissa la presentazione del preventivo: alla conferma il lead avanzerà a Presentato."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Data e ora *</Label>
              <Input type="datetime-local" value={apDate} onChange={(e) => setApDate(e.target.value)} data-testid="appointment-datetime" />
            </div>
            <div className="space-y-2">
              <Label>Durata (minuti)</Label>
              <Input type="number" min="15" step="15" value={apDuration} onChange={(e) => setApDuration(e.target.value)} data-testid="appointment-duration" />
            </div>
            <div className="space-y-2">
              <Label>Tecnico assegnato</Label>
              <Input value={apptCtx ? techName(apptCtx.lead.assigned_technician_id) : ''} disabled data-testid="appointment-technician" />
              <p className="text-xs text-[#999]">Per cambiare tecnico usa la riassegnazione dedicata.</p>
            </div>
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea value={apNotes} onChange={(e) => setApNotes(e.target.value)} rows={2} data-testid="appointment-notes" />
            </div>
            {apptCtx && !apptCtx.lead.email && (
              <div className="p-2 bg-[#FFF9E5] border border-[#FFE999] rounded text-xs text-[#B38C00]" data-testid="appointment-no-client-email">
                Il cliente non ha un indirizzo email: l'appuntamento verrà creato senza invio email al cliente.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApptCtx(null)} disabled={apSubmitting}>Annulla</Button>
            <Button className="btn-primary" onClick={submitAppointment} disabled={apSubmitting || !apDate} data-testid="appointment-confirm-btn">
              {apSubmitting ? 'Creo appuntamento...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Crea Lead (pipeline-aware) — F2.3 */}
      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="create-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-[#FF7819]" /> Aggiungi Lead</DialogTitle>
            <DialogDescription>Crea un nuovo lead direttamente nella pipeline selezionata.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Pipeline</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={cPipeline === 'call' ? 'default' : 'outline'}
                  className={cPipeline === 'call' ? 'btn-primary' : ''}
                  onClick={() => setCPipeline('call')} data-testid="create-pipeline-call">Chiamata</Button>
                <Button type="button" size="sm" variant={cPipeline === 'sales' ? 'default' : 'outline'}
                  className={cPipeline === 'sales' ? 'btn-primary' : ''}
                  onClick={() => setCPipeline('sales')} data-testid="create-pipeline-sales">Vendita</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Nome *</Label>
                <Input value={cForm.first_name} onChange={(e) => setCForm({ ...cForm, first_name: e.target.value })} data-testid="create-first-name" /></div>
              <div className="space-y-1"><Label>Cognome *</Label>
                <Input value={cForm.last_name} onChange={(e) => setCForm({ ...cForm, last_name: e.target.value })} data-testid="create-last-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Telefono *</Label>
                <Input value={cForm.phone} onChange={(e) => setCForm({ ...cForm, phone: e.target.value })} data-testid="create-phone" /></div>
              <div className="space-y-1"><Label>Email</Label>
                <Input value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} data-testid="create-email" /></div>
            </div>
            <div className="space-y-1"><Label>Città</Label>
              <Input value={cForm.customer_city} onChange={(e) => setCForm({ ...cForm, customer_city: e.target.value })} data-testid="create-city" /></div>
            {cPipeline === 'sales' && canSeeBoth && (
              <div className="space-y-1">
                <Label>Assegnatario *</Label>
                <Select value={cTech} onValueChange={(v) => onAssigneeChange(v, setCTech, cTech, cPM, setCPM)}>
                  <SelectTrigger data-testid="create-assignee-select"><SelectValue placeholder="Seleziona Tecnico o Project Manager" /></SelectTrigger>
                  <SelectContent>
                    {assigneeOptions.length === 0 && <SelectItem value="__none" disabled>Nessun assegnatario disponibile</SelectItem>}
                    {activeTechs.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Tecnici</SelectLabel>
                        {activeTechs.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                      </SelectGroup>
                    )}
                    {activePMs.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Project Manager (gestione diretta)</SelectLabel>
                        {activePMs.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            {cPipeline === 'sales' && canSeeBoth && (
              isPMId(cTech) ? (
                <div className="p-2.5 bg-[#FFF3EA] border border-[#FFD9BF] rounded text-xs text-[#C24E00] flex items-center gap-2" data-testid="create-direct-management">
                  <Handshake className="w-4 h-4 shrink-0" />
                  <span>Gestione diretta: <strong>{pmLabel(cTech)}</strong> è assegnatario e Project Manager.</span>
                </div>
              ) : (
                <div className="space-y-1">
                  <Label>Project Manager *</Label>
                  <Select value={cPM || ''} onValueChange={setCPM}>
                    <SelectTrigger data-testid="create-pm-select"><SelectValue placeholder="Seleziona Project Manager" /></SelectTrigger>
                    <SelectContent>
                      {activePMs.length === 0 && <SelectItem value="__none" disabled>Nessun PM attivo</SelectItem>}
                      {activePMs.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )
            )}
            {cPipeline === 'sales' && isTechnician && (
              <p className="text-xs text-[#737373]">Il lead sarà assegnato automaticamente a te.</p>
            )}
            <div className="space-y-1"><Label>Nota (opzionale)</Label>
              <Textarea value={cForm.notes} onChange={(e) => setCForm({ ...cForm, notes: e.target.value })} rows={2} data-testid="create-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={cSubmitting}>Annulla</Button>
            <Button className="btn-primary" onClick={submitCreate} disabled={cSubmitting} data-testid="create-lead-confirm-btn">
              {cSubmitting ? 'Creo...' : 'Crea Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadPipelineView;
